import { useCallback, useEffect, useState } from 'react';
import { useData, useRepo } from '../useStore.js';
import { usePrefs } from './usePrefs.js';
import Topbar from './Topbar.jsx';
import TypeBar from './TypeBar.jsx';
import Library from './Library.jsx';
import Editor from './Editor.jsx';

const START = { view: 'library', spaceId: null, docId: null, tool: 'write', mode: 'write', ask: false, focus: false };

export default function App({ syncStatus, onSyncNow }) {
  const repo = useRepo();
  const data = useData();
  const [nav, setNav] = useState(START);
  const [prefs, setPrefs, resetPrefs] = usePrefs();

  const go = useCallback(patch => setNav(n => ({ ...n, ...patch })), []);

  const space = data.spaces.find(s => s.id === nav.spaceId) || null;
  const doc = space ? space.docs.find(d => d.id === nav.docId) || null : null;
  const isEditor = nav.view === 'editor';
  const chromeOn = !(isEditor && nav.focus);

  // A space whose open document was deleted elsewhere shouldn't keep pointing at it.
  useEffect(() => {
    if (nav.docId && space && !space.docs.some(d => d.id === nav.docId)) go({ docId: null });
  }, [nav.docId, space, go]);

  // The space may vanish underneath us — deleted here, or on another device
  // and arrived by sync. Either way, the editor has nothing left to show.
  useEffect(() => {
    if (isEditor && nav.spaceId && !space) go({ view: 'library', spaceId: null, docId: null });
  }, [isEditor, nav.spaceId, space, go]);

  useEffect(() => {
    if (!nav.focus) return;
    const onKey = e => { if (e.key === 'Escape') go({ focus: false }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav.focus, go]);

  const openSpace = useCallback(sp => go({
    view: 'editor', spaceId: sp.id, docId: sp.docs[0]?.id ?? null,
    tool: 'write', mode: 'write', focus: false
  }), [go]);

  const newSpace = useCallback(() => {
    const created = repo.addSpace({ name: '', kind: '' });
    // Straight to its settings with the name focused: you name the book before
    // you write in it, and an unnamed space on the desk reads as a mistake.
    go({ view: 'editor', spaceId: created.id, docId: null, tool: 'goals', mode: 'write', focus: false });
  }, [repo, go]);

  const newDoc = useCallback(() => {
    if (!space) return;
    const title = space.id === 'journal'
      ? new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      : '';
    const created = repo.addDocument(space.id, { title });
    go({ docId: created.id, focusTitle: created.id, tool: 'write', mode: 'write' });
    return created;
  }, [repo, space, go]);

  return (
    <div className="app">
      {chromeOn && (
        <Topbar
          nav={nav} go={go} space={space} spaces={data.spaces}
          prefs={prefs} setPrefs={setPrefs}
          syncStatus={syncStatus} onSyncNow={onSyncNow}
        />
      )}
      {chromeOn && isEditor && prefs.typeBar && (
        <TypeBar prefs={prefs} setPrefs={setPrefs} reset={resetPrefs} />
      )}

      {nav.view === 'library'
        ? <Library spaces={data.spaces} onOpen={openSpace} onNew={newSpace} />
        : <Editor
            nav={nav} go={go} space={space} doc={doc} chromeOn={chromeOn}
            prefs={prefs} setPrefs={setPrefs}
            newDoc={newDoc} onSpaceDeleted={() => go({ view: 'library', spaceId: null, docId: null })}
          />}

      {isEditor && nav.focus && (
        <button className="focus-exit" onClick={() => go({ focus: false })}>Exit focus · esc</button>
      )}
    </div>
  );
}
